import React, { FC, useCallback, useMemo } from 'react';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import WorldIcon from '@atlaskit/icon/glyph/world';
import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';
import { token } from '@atlaskit/tokens';
import { SloGroupCohortSummaryDTO } from '@atlassiansox/phobos-client-ts';
import Pagination from '@atlaskit/pagination';
import { useQuery } from '@tanstack/react-query';
import { compact } from 'lodash';
import HelpIcon from '@atlaskit/icon/glyph/question-circle';

import {
  flexStart,
  spaceGapCompact,
  spaceTopCozy,
  colors,
  marginPushRight,
  positionRelative,
} from '@/styles';
import { KeyValue } from '@/components/primitive';
import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';
import { useFetchErrorController } from '@/controllers';
import { Params } from '@/components/composed/catalog-search-bar/types';
import { getPagesFromTotalPage } from '@/components/composed/search/utils';
import { mapParamsToAPIQuery } from '@/components/composed/catalog-search-bar/services/utils';
import SearchPageSkeleton from '@/components/composed/search/search-results/search-page-skeleton';
import { DAC_DOCS_SLO_GROUP_COHORTS_URL } from '@/constants';

import { BASE_SLOS_PAGE_SIZE } from '../../constants';
import { generalBarStyles, badgeStyles } from '../../styles';
import { getAnchorIDFromCohortType } from '../constants';
import SLOCohortMembers from '../slo-cohort-members';
import {
  iconSizeAlignStyles,
  iconSizeAlignFixStyles,
  anchorStyles,
  cohortTitleStyles,
} from '../styles';

type SLOCohortDynamicProps = {
  sloGroupCohortSummary: SloGroupCohortSummaryDTO;
  publicId: string;
  hasBreachStatusOnly?: string | undefined;
  cohorts: Map<string, string>;
  isRootCohort?: boolean;
  cohortTypeSorted: string[];
};

const SLOCohortDynamic: FC<SLOCohortDynamicProps> = ({
  sloGroupCohortSummary,
  publicId,
  hasBreachStatusOnly,
  cohorts,
  isRootCohort,
  cohortTypeSorted,
}) => {
  const { cohort, draft } = sloGroupCohortSummary;
  const cohortIcon = useMemo(() => {
    if (isRootCohort) {
      return (
        <span css={[iconSizeAlignStyles, iconSizeAlignFixStyles]}>
          <WorldIcon
            primaryColor={token('color.text.subtlest', colors.N100)}
            label=""
            size="small"
          />
        </span>
      );
    }
    return <BulletListIcon primaryColor={token('color.text.subtlest', colors.N100)} label="" />;
  }, [isRootCohort]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const { onError } = useFetchErrorController();

  const handleChangePage = useCallback((_: any, page: number) => {
    setCurrentPage(page);
  }, []);

  const { queryKey, path } = useMemo(() => {
    const param: string = `&cohortTypes=${cohort}&${mapParamsToAPIQuery(
      {} as Params,
      false
    )}&size=${BASE_SLOS_PAGE_SIZE}${
      hasBreachStatusOnly ? '&hasBreachStatusOnly=true' : ''
    }&${`cohortMembers=${compact(Array.from(cohorts.values())).join(',')}`}`;

    return {
      ...querySpec('slo-group.{id}.members.{params}.{page}', {
        id: publicId,
        param,
        page: currentPage.toString(),
      }),
    };
  }, [publicId, hasBreachStatusOnly, currentPage, cohort, cohorts]);

  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const pages: number[] = useMemo(() => getPagesFromTotalPage(data?.totalPages), [data]);
  const showPagination: boolean = (data?.totalPages ?? 0) > 1;

  if (isLoading) return <SearchPageSkeleton isCompact />;
  if (!data?.content?.length) return null;

  return (
    <div css={positionRelative}>
      <div css={[anchorStyles]} id={getAnchorIDFromCohortType(cohort)} />
      <div css={[flexStart, spaceGapCompact, generalBarStyles, cohortTitleStyles]}>
        {cohortIcon}
        <KeyValue noPositionFix title="cohort">
          <Badge>
            <span css={[badgeStyles]}>{cohort}</span>
          </Badge>
        </KeyValue>
        {draft && <Lozenge appearance="moved">draft</Lozenge>}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${DAC_DOCS_SLO_GROUP_COHORTS_URL}#${cohort.toLowerCase()}`}
        >
          <HelpIcon primaryColor={token('color.icon.accent.gray')} size="small" label="Help" />
        </a>
        {showPagination && (
          <div css={marginPushRight}>
            <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={handleChangePage} />
          </div>
        )}
      </div>
      <div css={spaceTopCozy}>
        <SLOCohortMembers slos={data.content} cohortTypeSorted={cohortTypeSorted} isDynamic />
      </div>
    </div>
  );
};

export default SLOCohortDynamic;
